import React, { useEffect, useRef, useState } from "react";
import "./AmazonIVSPlayer.css";

const IVS_SCRIPT_ID = "amazon-ivs-player-script";
const IVS_SCRIPT_SRC =
  "https://player.live-video.net/1.52.1/amazon-ivs-player.min.js";

const loadIvsScript = () => {
  return new Promise((resolve, reject) => {
    if (window.IVSPlayer) {
      resolve(window.IVSPlayer);
      return;
    }

    const existingScript = document.getElementById(IVS_SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.IVSPlayer));
      existingScript.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.id = IVS_SCRIPT_ID;
    script.src = IVS_SCRIPT_SRC;
    script.async = true;

    script.onload = () => resolve(window.IVSPlayer);
    script.onerror = reject;

    document.body.appendChild(script);
  });
};

const AmazonIVSPlayer = ({
  playbackUrl,
  autoPlay = true,
  muted = false,
  controls = true,
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  const [playerError, setPlayerError] = useState("");
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [showManualPlay, setShowManualPlay] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const setupPlayer = async () => {
      try {
        setPlayerError("");
        setIsPlayerReady(false);
        setShowManualPlay(false);

        if (!playbackUrl) {
          setPlayerError("Playback URL is missing.");
          return;
        }

        const IVSPlayer = await loadIvsScript();

        if (!IVSPlayer?.isPlayerSupported) {
          setPlayerError("Amazon IVS Player is not supported in this browser.");
          return;
        }

        const player = IVSPlayer.create();

        playerRef.current = player;

        player.attachHTMLVideoElement(videoRef.current);

        if (typeof player.setAutoplay === "function") {
          player.setAutoplay(autoPlay);
        }

        if (typeof player.setMuted === "function") {
          player.setMuted(muted);
        }

        if (typeof player.setRebufferToLive === "function") {
          player.setRebufferToLive(true);
        }

        player.addEventListener(IVSPlayer.PlayerState.READY, () => {
          if (!isMounted) return;
          setIsPlayerReady(true);
        });

        player.addEventListener(IVSPlayer.PlayerState.PLAYING, () => {
          if (!isMounted) return;
          setShowManualPlay(false);
        });

        player.addEventListener(IVSPlayer.PlayerEventType.ERROR, (error) => {
          console.error("Amazon IVS Player error:", error);

          if (!isMounted) return;

          setPlayerError("Live stream playback error. Please try refreshing.");
        });

        player.load(playbackUrl);

        if (autoPlay) {
          try {
            player.play();
          } catch (playError) {
            console.warn("Autoplay blocked:", playError);

            if (isMounted) {
              setShowManualPlay(true);
            }
          }
        }
      } catch (error) {
        console.error("Failed to setup Amazon IVS Player:", error);

        if (isMounted) {
          setPlayerError("Failed to load Amazon IVS Player.");
        }
      }
    };

    setupPlayer();

    return () => {
      isMounted = false;

      if (playerRef.current) {
        playerRef.current.delete();
        playerRef.current = null;
      }
    };
  }, [playbackUrl, autoPlay, muted]);

  const handleManualPlay = () => {
    try {
      playerRef.current?.play();
      setShowManualPlay(false);
    } catch (error) {
      console.error("Manual play failed:", error);
      setPlayerError("Could not start playback.");
    }
  };

  return (
    <div className="amazon-ivs-player">
      <video
        ref={videoRef}
        className="amazon-ivs-player-video"
        controls={controls}
        playsInline
      />

      {!isPlayerReady && !playerError && (
        <div className="amazon-ivs-player-overlay">
          <p>Loading live stream...</p>
        </div>
      )}

      {showManualPlay && !playerError && (
        <div className="amazon-ivs-player-overlay">
          <button type="button" onClick={handleManualPlay}>
            Play Live Stream
          </button>
        </div>
      )}

      {playerError && (
        <div className="amazon-ivs-player-overlay amazon-ivs-player-error">
          <p>{playerError}</p>
        </div>
      )}
    </div>
  );
};

export default AmazonIVSPlayer;
